import { isRejectedWithValue } from "@reduxjs/toolkit";
import type { Middleware } from "@reduxjs/toolkit";
import { GET_ERRORS } from "./errorServices/errorTypes";
import { isDeleteRefusal } from "./deleteRefusal";

/**
 * Central error surfacing for the RTK write-thunk pilot (SCRUM-840).
 *
 * Any `createAppAsyncThunk` thunk that rejects carries `meta.appError` and a
 * displayable `payload`. This middleware feeds that payload into the existing
 * `GET_ERRORS` → `errors` slice → `GlobalNotifications` hub, so individual
 * thunks and their callers no longer each wire a `catch → GET_ERRORS`.
 *
 * It keys on the marker meta on purpose: the hand-migrated sites/pages thunks
 * (SCRUM-596/722) use plain `createAsyncThunk` with their own `GET_ERRORS`
 * dispatch and are NOT marked, so they are not surfaced a second time here.
 *
 * A delete refused with a `referrers` list (the SCRUM-2350 409) is the one
 * marked rejection it leaves alone: the screen answers that with the
 * ReferrersDialog pop-up, and a toast of the same message beside it would show
 * it twice (SCRUM-2352).
 */
export const appErrorMiddleware: Middleware = (storeApi) => (next) => (action) => {
  const result = next(action);
  if (
    isRejectedWithValue(action) &&
    (action.meta as { appError?: boolean } | undefined)?.appError &&
    !isDeleteRefusal(action.payload)
  ) {
    storeApi.dispatch({
      type: GET_ERRORS,
      payload: action.payload,
    });
  }
  return result;
};

